import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { subDays, format as formatDate } from 'date-fns';
import { restartableTask, timeout } from 'ember-concurrency';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import { not } from 'ember-truth-helpers';
import EmbeddedApp from 'garaje/components/embedded-app';
import ExportStatus, { EXPORT_STATUS, type Status } from 'garaje/components/export-status';
import UiClassicModalDialog from 'garaje/components/ui/classic/modal-dialog';
import config from 'garaje/config/environment';
import type FlowModel from 'garaje/models/flow';
import type AjaxService from 'garaje/services/ajax';
import type CurrentLocationService from 'garaje/services/current-location';
import type MessageBusService from 'garaje/services/message-bus';
import type StateService from 'garaje/services/state';
import urlBuilder from 'garaje/utils/url-builder';

interface AnalyticsExportModalMessage {
  event: 'showModal' | 'closeModal' | 'exportCreated';
  exportType?: 'visitors' | 'invites' | 'employees';
  query?: {
    flow_id?: string;
    start?: string;
    end?: string;
  };
  label?: string;
  exportId?: string;
}

type ExportStatusType = 'PENDING' | 'COMPLETE' | 'ERROR';

interface ExportStatusResponse {
  jobKey: string;
  status: ExportStatusType;
  logType: 'VISITORS' | 'INVITES' | 'EMPLOYEES';
}

interface AnalyticsExportModalSignature {
  Element: HTMLDivElement;
  Args: {
    // For log pages (pass @isOpen to control externally)
    isOpen?: boolean;
    onClose?: () => void;
    scope?: 'location' | 'global';
    exportType?: 'visitors' | 'invites' | 'employees';
    selectedFlows?: string; // Comma-separated flow IDs for multi-flow selection
    flows?: FlowModel[];
    locationFlowIds?: string[];
    walkinFlowId?: string;
    // Optional: directly provide flowId, label, or locationId (overrides computed values)
    flowId?: string;
    label?: string;
    locationId?: string;
    // Optional: override default date range (defaults to last 30 days)
    startDate?: string;
    endDate?: string;

    // For analytics dashboards: don't pass @isOpen - component listens to messages
  };
}

/**
 * A component that renders an export modal for analytics data with improved export URLs.
 *
 * It encapsulates the logic for building improved export URLs and rendering the embedded app
 * within a modal, reducing duplication across multiple controllers and templates by computing
 * company IDs, location IDs, flow IDs, labels, and dates internally.
 *
 * @example
 * ```hbs
 * <Analytics::ExportModal
 *   @isOpen={{this.showExportModal}}
 *   @onClose={{this.toggleModal}}
 *   @scope="location"
 *   @exportType="invites"
 *   @selectedFlows={{this.selectedFlows}}
 *   @flows={{this.model.flows}}
 *   @locationFlowIds={{this.model.locationFlowIds}}
 * />
 * ```
 */
export default class AnalyticsExportModal extends Component<AnalyticsExportModalSignature> {
  @service declare state: StateService;
  @service declare currentLocation: CurrentLocationService;
  @service declare messageBus: MessageBusService;
  @service declare ajax: AjaxService;

  // For message mode
  @tracked isModalOpen = false;
  @tracked _exportType: 'visitors' | 'invites' | 'employees' = 'visitors';
  @tracked _flowId = '';
  @tracked _startDate = '';
  @tracked _endDate = '';
  @tracked _label = '';

  // Export status notification state
  @tracked showExportStatus = false;
  @tracked exportStatusState: Status = EXPORT_STATUS.STARTED;
  @tracked exportJobKey = '';
  @tracked exportLogType: 'VISITORS' | 'INVITES' | 'EMPLOYEES' = 'VISITORS';
  @tracked exportId = '';
  @tracked exportErrorDetail = '';

  constructor(owner: unknown, args: AnalyticsExportModalSignature['Args']) {
    super(owner, args);
    // Always listen to messages (for closeModal from export iframe)
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  willDestroy(): void {
    super.willDestroy();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.off('embedded-app-message', this, this.handleMessage);
  }

  @action
  handleMessage(message: AnalyticsExportModalMessage): void {
    if (message.event === 'showModal') {
      // Handle standardized showModal event
      // For dashboard pages, exportType comes from message
      // For log pages, @exportType is passed as prop
      if (message.exportType) {
        this._exportType = message.exportType;
      }
      this._flowId = message.query?.flow_id || '';
      this._startDate = message.query?.start || '';
      this._endDate = message.query?.end || '';
      this._label = message.label || '';
      this.isModalOpen = true;
    } else if (message.event === 'closeModal') {
      // Handle close event (from export iframe inside modal)
      this.closeModal();
    } else if (message.event === 'exportCreated') {
      // Handle export creation and start polling for status
      if (message.exportId) {
        this.exportId = message.exportId;
        // Clear any previous error detail
        this.exportErrorDetail = '';
        // Show export status notification immediately
        this.showExportStatus = true;
        this.exportStatusState = EXPORT_STATUS.STARTED;

        // Wait 300ms, then close modal
        setTimeout(() => {
          this.closeModal();
        }, 300);

        // Start polling for export status
        void this.pollExportStatus.perform(message.exportId);
      }
    }
  }

  @action
  closeModal(): void {
    this.isModalOpen = false;
    this.args.onClose?.();
  }

  get downloadUrl(): string {
    const companyId = this.state.currentCompany.id;
    return urlBuilder.exportDownloadUrl(companyId, this.exportId);
  }

  @action
  triggerDownload(): void {
    // Use anchor tag with download attribute to trigger download
    const anchor = document.createElement('a');
    anchor.href = this.downloadUrl;
    anchor.download = '';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  get isInMessageMode(): boolean {
    // If @isOpen is not provided, we're in message mode (analytics dashboards)
    return this.args.isOpen === undefined;
  }

  get isOpen(): boolean {
    // If @isOpen is provided, use it (log pages)
    // Otherwise use internal state (analytics dashboards listening to messages)
    return this.isInMessageMode ? this.isModalOpen : this.args.isOpen ?? false;
  }

  get exportType(): 'visitors' | 'invites' | 'employees' {
    // If @exportType is provided (log pages), use it
    // Otherwise use _exportType (dashboard pages in message mode)
    return this.args.exportType ?? this._exportType;
  }

  get companyId(): string {
    return this.state.currentCompany.id;
  }

  get locationId(): string | undefined {
    // If locationId is provided directly, use it (for multi-location exports)
    if (this.args.locationId !== undefined) {
      return this.args.locationId;
    }

    return this.args.scope === 'location' ? this.currentLocation.location.id : undefined;
  }

  get flowId(): string | undefined {
    // If in message mode, use message data
    if (this.isInMessageMode) {
      return this._flowId;
    }

    // If flowId is provided directly, use it
    if (this.args.flowId !== undefined) {
      return this.args.flowId;
    }

    const { selectedFlows, locationFlowIds, walkinFlowId } = this.args;

    // If specific flows are selected, return them
    if (selectedFlows && selectedFlows !== '') {
      return selectedFlows;
    }

    // If no flow is selected, return all location flow IDs
    if (locationFlowIds) {
      const ids = [...locationFlowIds];
      // Add walkin flow ID if provided
      if (walkinFlowId) {
        ids.push(walkinFlowId);
      }
      return ids.join(',');
    }

    // Fallback: if flows are provided, return all flow IDs
    if (this.args.flows) {
      return this.args.flows.map((flow) => flow.id).join(',');
    }

    return undefined;
  }

  get label(): string {
    // If in message mode, use message data
    if (this.isInMessageMode) {
      return this._label;
    }

    // If label is provided directly, use it
    if (this.args.label) {
      return this.args.label;
    }

    const { selectedFlows, flows } = this.args;

    // If no flows selected, return "All" label
    if (!selectedFlows || selectedFlows === '') {
      return 'All visitor types';
    }

    // If flows are selected, generate a label
    const flowIds = selectedFlows.split(',').filter((id) => id !== '');

    if (!flows || flowIds.length === 0) {
      return 'All visitor types';
    }

    // If only one flow selected, use its name
    if (flowIds.length === 1) {
      const flow = flows.find((f) => f.id === flowIds[0]);
      return flow?.name || 'Selected visitor type';
    }

    // Multiple flows selected
    return `${flowIds.length} visitor types`;
  }

  get startDate(): string {
    // If in message mode, use message data
    if (this.isInMessageMode && this._startDate) {
      return this._startDate;
    }

    // Use provided startDate or default to 30 days ago
    if (this.args.startDate) return this.args.startDate;
    return formatDate(subDays(new Date(), 30), 'yyyy-MM-dd');
  }

  get endDate(): string {
    // If in message mode, use message data
    if (this.isInMessageMode && this._endDate) {
      return this._endDate;
    }

    // Use provided endDate or default to today
    if (this.args.endDate) return this.args.endDate;
    return formatDate(new Date(), 'yyyy-MM-dd');
  }

  get exportUrl(): string {
    const scope = this.args.scope ?? 'location';
    const exportType = this.exportType;
    const companyId = this.companyId;
    const locationId = this.locationId;
    const startDate = this.startDate;
    const endDate = this.endDate;
    const loginRequired = true;

    // Build improved export URLs based on scope and export type
    if (scope === 'location') {
      return urlBuilder.exportModalLocationUrlV2(companyId, locationId!, startDate, endDate, loginRequired, exportType);
    } else if (scope === 'global') {
      return urlBuilder.exportModalGlobalUrlV2(companyId, startDate, endDate, loginRequired, exportType);
    }

    // Fallback (should never reach here)
    return '';
  }

  get exportContext():
    | {
        flowIds?: string[];
        label?: string;
      }
    | undefined {
    const exportType = this.exportType;

    // Only pass context for 'visitors' and 'invites' types when a subset of flows is selected
    if (exportType === 'visitors' || exportType === 'invites') {
      // In message mode, check if flowId was provided in the message
      if (this.isInMessageMode && this._flowId) {
        return {
          flowIds: this._flowId.split(','),
          label: this._label,
        };
      }

      // For log pages, check if a specific flow is selected (not "all flows")
      const { selectedFlows } = this.args;
      if (selectedFlows && selectedFlows !== '') {
        const flowId = this.flowId;
        const label = this.label;
        return {
          flowIds: flowId ? flowId.split(',') : [],
          label: label,
        };
      }
    }

    return undefined;
  }

  /**
   * Poll export status endpoint every 2 seconds until the export is complete or has errored
   */
  pollExportStatus = restartableTask(async (exportId: string) => {
    const companyId = this.state.currentCompany.id;
    const baseUrl = config.sigmaAnalyticsHost;
    const statusUrl = `${baseUrl}/api/v1/c/${companyId}/exports/${encodeURIComponent(exportId)}/status`;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const response = await this.ajax.request<ExportStatusResponse>(statusUrl, {
          contentType: 'application/json',
        });
        const { jobKey, status, logType } = response;

        // Update tracked state
        this.exportJobKey = jobKey;
        this.exportLogType = logType;

        // Check if export is done
        if (status === 'COMPLETE') {
          this.exportStatusState = EXPORT_STATUS.COMPLETE;
          // Automatically trigger download
          this.triggerDownload();
          break;
        } else if (status === 'ERROR') {
          this.exportStatusState = EXPORT_STATUS.FAILED;
          // Set a default error detail if not already set
          if (!this.exportErrorDetail) {
            this.exportErrorDetail = 'Something went wrong';
          }
          break;
        }

        // Wait 2 seconds before polling again
        await timeout(2000);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error polling export status:', error);
        this.exportStatusState = EXPORT_STATUS.FAILED;
        // Extract error message from the error response
        if (error && typeof error === 'object' && 'detail' in error && typeof error.detail === 'string') {
          this.exportErrorDetail = error.detail;
        } else if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
          this.exportErrorDetail = error.message;
        } else {
          this.exportErrorDetail = 'Something went wrong';
        }
        break;
      }
    }
  });

  get modalId(): string {
    const scope = this.args.scope ?? 'location';
    const exportType = this.exportType;
    return `${scope}-${exportType}-export`;
  }

  get exportStatusTitle(): string {
    switch (this.exportStatusState) {
      case EXPORT_STATUS.STARTED:
        return 'Export started';
      case EXPORT_STATUS.COMPLETE:
        return 'Export complete';
      case EXPORT_STATUS.FAILED:
        return 'Export failed';
      default:
        return 'Export';
    }
  }

  get exportStatusMessage(): string {
    const dataType = this.exportLogType.toLowerCase();

    switch (this.exportStatusState) {
      case EXPORT_STATUS.STARTED:
        return `Your ${dataType} data is being prepared. We'll email you when it's ready.`;
      case EXPORT_STATUS.COMPLETE:
        return `Your ${dataType} data is ready! If it didn't download, check your email or download it`;
      case EXPORT_STATUS.FAILED: {
        const reason = this.exportErrorDetail || 'Something went wrong';
        // If it's a generic error, don't show "due to" and reason
        if (reason === 'Something went wrong') {
          return `Your ${dataType} data couldn't be exported. Please try again.`;
        }
        return `Your ${dataType} data couldn't be exported due to ${reason}. Please try again.`;
      }
      default:
        return '';
    }
  }

  get exportStatusLinkText(): string {
    return this.exportStatusState === EXPORT_STATUS.COMPLETE ? 'here.' : '';
  }

  get exportStatusLinkHref(): string {
    return this.exportStatusState === EXPORT_STATUS.COMPLETE ? this.downloadUrl : '';
  }

  @action
  handleExportStatusClose(): void {
    this.showExportStatus = false;
  }

  <template>
    {{#if this.isOpen}}
      <UiClassicModalDialog
        @clickOutsideToClose={{not this.showExportStatus}}
        @onClose={{this.closeModal}}
        @translucentOverlay={{true}}
        @containerClass="!max-w-export"
      >
        <FocusTrapper>
          <EmbeddedApp
            @id={{this.modalId}}
            @url={{this.exportUrl}}
            @context={{this.exportContext}}
            class="-mx-8"
            data-test-export-iframe
          />
        </FocusTrapper>
      </UiClassicModalDialog>
    {{/if}}

    {{#if this.showExportStatus}}
      <ExportStatus
        @position="bottom-right"
        @status={{this.exportStatusState}}
        @title={{this.exportStatusTitle}}
        @message={{this.exportStatusMessage}}
        @linkText={{this.exportStatusLinkText}}
        @linkHref={{this.exportStatusLinkHref}}
        @onClick={{this.handleExportStatusClose}}
      />
    {{/if}}
  </template>
}
